import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Printer, FileDown, Loader2, Filter, AlertTriangle, Clock, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Shared Food Prep report modal.
 *
 * Two variants:
 *   • "active" — right-now snapshot grouped Expired → Expiring → Other.
 *     Includes a 3/7/14-day filter that re-fetches on change.
 *   • "monthly" — monthly Consumption vs Wastage summary. Month comes
 *     from the caller (already picked in the Historical Data tab), so
 *     the modal shows it as a fixed badge, not a picker.
 *
 * Both variants print A4 via `window.print()` with a print-only
 * stylesheet scoped to `.fp-report-print`, and both offer a "Save PDF"
 * button that downloads the ReportLab-generated Audit-Vault-style PDF.
 */
const HORIZONS = [3, 7, 14];

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
function qty(it) {
  const r = it.quantity_remaining ?? 1;
  const t = it.quantity_total ?? r;
  return t !== r ? `${r}/${t}` : `${r}`;
}

export default function FoodPrepReportModal({ variant, month, onClose }) {
  const [horizon, setHorizon] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingPdf, setSavingPdf] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const url = variant === "active"
          ? `/food-prep/reports/active-inventory?horizon_days=${horizon}`
          : `/food-prep/reports/monthly?month=${encodeURIComponent(month)}`;
        const { data: d } = await api.get(url);
        if (!cancel) setData(d);
      } catch (e) {
        if (!cancel) toast.error(e.response?.data?.detail || "Couldn't load report");
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [variant, month, horizon]);

  const printA4 = () => {
    // Print stylesheet is scoped by `.fp-report-print` so nothing outside
    // the report body affects the printed page.
    const el = printRef.current;
    if (!el) return;
    document.body.classList.add("fp-print-mode");
    window.print();
    document.body.classList.remove("fp-print-mode");
  };

  const savePdf = async () => {
    setSavingPdf(true);
    try {
      const url = variant === "active"
        ? `/food-prep/reports/active-inventory.pdf?horizon_days=${horizon}`
        : `/food-prep/reports/monthly.pdf?month=${encodeURIComponent(month)}`;
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = variant === "active"
        ? `food_prep_active_inventory_${horizon}d.pdf`
        : `food_prep_monthly_${month}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't generate PDF");
    } finally { setSavingPdf(false); }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center p-4 fp-modal-overlay"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="fp-report-modal"
    >
      <div className="vb-card p-0 w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden fp-modal-body">
        <div className="flex items-center justify-between p-4 border-b fp-no-print">
          <div>
            <h2 className="text-lg font-semibold m-0">
              {variant === "active" ? "Active inventory report" : "Monthly consumption vs wastage"}
            </h2>
            <p className="text-xs opacity-70 m-0 mt-0.5">
              {variant === "active"
                ? "Snapshot of items expiring soonest. Print or save as PDF."
                : `Period: ${month}`}
            </p>
          </div>
          <button className="vb-btn ghost" onClick={onClose} data-testid="fp-report-close">
            <X size={16} />
          </button>
        </div>

        {variant === "active" && (
          <div className="px-4 py-2 border-b flex items-center gap-2 fp-no-print" data-testid="fp-report-filters">
            <Filter size={13} className="opacity-60" />
            <span className="text-xs opacity-70">Expiry window:</span>
            {HORIZONS.map((n) => (
              <button
                key={n}
                className={`vb-btn sm ${n === horizon ? "" : "ghost"}`}
                onClick={() => setHorizon(n)}
                data-testid={`fp-horizon-${n}`}
                style={{ padding: "3px 10px", fontSize: 12 }}
              >
                {n} days
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6 fp-report-print" ref={printRef}>
          {loading || !data ? (
            <div className="text-sm opacity-60 py-8 text-center">
              <Loader2 className="animate-spin inline-block mr-2" size={16} /> Loading report…
            </div>
          ) : variant === "active" ? (
            <ActiveInventoryBody data={data} horizon={horizon} />
          ) : (
            <MonthlyBody data={data} month={month} />
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t fp-no-print">
          <button className="vb-btn ghost" onClick={savePdf} disabled={savingPdf || loading} data-testid="fp-save-pdf">
            {savingPdf ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
            Save PDF
          </button>
          <button className="vb-btn" onClick={printA4} disabled={loading} data-testid="fp-print-a4">
            <Printer size={14} /> Print A4
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function KPI({ v, label, tone }) {
  const color = tone === "err" ? "#dc2626" : tone === "warn" ? "#c2410c" : tone === "ok" ? "#16a34a" : "var(--vb-text-2)";
  return (
    <div className="rounded-lg p-3 text-center" style={{ background: "var(--vb-bg)", border: "1px solid var(--vb-border)" }}>
      <div className="text-2xl font-extrabold leading-none" style={{ color }}>{v}</div>
      <div className="text-[10px] uppercase tracking-wider opacity-60 mt-1">{label}</div>
    </div>
  );
}

function BucketSection({ icon: Icon, tone, title, items, empty }) {
  return (
    <div className="mb-5 fp-avoid-break">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} style={{ color: tone === "err" ? "#dc2626" : tone === "warn" ? "#c2410c" : "#16a34a" }} />
        <h3 className="text-base font-semibold m-0">{title}</h3>
        <span className="text-xs opacity-60">({items.length})</span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs opacity-50 italic pl-6">{empty}</p>
      ) : (
        <table className="vb-table w-full text-sm">
          <thead>
            <tr>
              <th>Site</th><th>Facility</th><th>Item</th><th>Qty</th><th>Prepared</th><th>Use by</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id}>
                <td className="text-xs">{it.site_name || "—"}</td>
                <td className="text-xs">{it.facility_name || "—"}</td>
                <td className="text-sm font-medium">{it.name}</td>
                <td className="text-xs">{qty(it)}</td>
                <td className="text-xs whitespace-nowrap">{fmt(it.prepared_at)}</td>
                <td className="text-xs whitespace-nowrap">{fmt(it.use_by)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ActiveInventoryBody({ data, horizon }) {
  const t = data.totals || {};
  return (
    <div>
      <div className="mb-4 pb-3" style={{ borderBottom: "2px solid var(--vb-accent, #783D19)" }}>
        <h1 className="text-2xl font-bold m-0" style={{ color: "var(--vb-accent, #783D19)" }}>Food Prep · Active Inventory</h1>
        <p className="text-xs opacity-70 mt-1">Snapshot · expiring window: {horizon} days · generated {fmt(data.generated_at)}</p>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-5" data-testid="fp-report-kpis">
        <KPI v={t.expired || 0} label="Expired units" tone="err" />
        <KPI v={t.expiring || 0} label={`Expiring ≤${horizon}d`} tone="warn" />
        <KPI v={t.other || 0} label="Other active" />
        <KPI v={(t.batches_expired || 0) + (t.batches_expiring || 0) + (t.batches_other || 0)} label="Total batches" />
      </div>
      <BucketSection icon={AlertTriangle} tone="err" title="Expired" items={data.expired || []} empty="No expired items." />
      <BucketSection icon={Clock} tone="warn" title={`Expiring within ${horizon} days`} items={data.expiring || []} empty="Nothing expiring in this window." />
      <BucketSection icon={CheckCircle2} tone="ok" title="Other active items" items={data.other || []} empty="No other active items." />
    </div>
  );
}

function MonthlyBody({ data, month }) {
  const t = data.totals || {};
  const consumedPct = Number(t.consumed_pct || 0);
  const wastagePct = Number(t.wastage_pct || 0);
  const monthLabel = useMemo(() => {
    try { return new Date(month + "-01").toLocaleDateString(undefined, { month: "long", year: "numeric" }); }
    catch { return month; }
  }, [month]);
  return (
    <div>
      <div className="mb-4 pb-3" style={{ borderBottom: "2px solid var(--vb-accent, #783D19)" }}>
        <h1 className="text-2xl font-bold m-0" style={{ color: "var(--vb-accent, #783D19)" }}>Food Prep · Monthly Summary</h1>
        <p className="text-xs opacity-70 mt-1">Period · {monthLabel}</p>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-5">
        <KPI v={t.prepared_batches || 0} label="Batches prepared" />
        <KPI v={t.prepared_units || 0} label="Units prepared" />
        <KPI v={t.disposed_units || 0} label="Units disposed" />
        <KPI v={t.on_time_units || 0} label="On-time" tone="ok" />
        <KPI v={t.late_units || 0} label="Overdue" tone={t.late_units ? "err" : undefined} />
        <KPI v={t.active_units || 0} label="Still active" />
      </div>

      <h3 className="text-base font-semibold mt-2 mb-2">Consumption vs Wastage</h3>
      <div className="w-full rounded-full overflow-hidden flex" style={{ height: 14, background: "var(--vb-bg)" }}>
        <div style={{ width: `${consumedPct}%`, background: "#16a34a" }} />
        <div style={{ width: `${wastagePct}%`, background: "#dc2626" }} />
      </div>
      <p className="text-xs mt-2">
        <span style={{ color: "#16a34a", fontWeight: 600 }}>{consumedPct.toFixed(1)}% consumed</span>
        {" · "}
        <span style={{ color: "#dc2626", fontWeight: 600 }}>{wastagePct.toFixed(1)}% wasted</span>
        {" · "}{t.consumed_units || 0} served/used · {t.wastage_units || 0} discarded
      </p>

      {(data.reason_breakdown || []).length > 0 && (
        <div className="mt-5 fp-avoid-break">
          <h3 className="text-base font-semibold mb-2">Wastage by reason</h3>
          <table className="vb-table w-full text-sm">
            <thead><tr><th>Reason</th><th>Units</th><th>Batches</th><th>% of disposed</th></tr></thead>
            <tbody>
              {data.reason_breakdown.map((r) => (
                <tr key={r.reason}>
                  <td>{(r.reason || "—").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</td>
                  <td>{r.units}</td>
                  <td>{r.batches}</td>
                  <td>{r.pct.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-5">
        <h3 className="text-base font-semibold mb-2">Item list</h3>
        {(data.items || []).length === 0 ? (
          <p className="text-xs opacity-60 italic">No food-prep transactions in {monthLabel}.</p>
        ) : (
          <table className="vb-table w-full text-sm" data-testid="fp-monthly-items">
            <thead><tr><th>Item</th><th>Site</th><th>Prepared</th><th>Use by</th><th>Status</th><th>Outcome</th></tr></thead>
            <tbody>
              {data.items.map((it) => {
                const disposed = it.status === "disposed";
                const reason = (it.disposal_reason || "").toLowerCase();
                const isConsumed = disposed && reason === "consumed";
                const status = isConsumed ? "Consumed"
                  : disposed ? `Disposed${reason ? " · " + reason.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : ""}`
                  : (it.state || "active").replace(/^\w/, (c) => c.toUpperCase());
                const outcome = isConsumed ? "Consumed"
                  : disposed ? (it.late ? "Overdue" : "On-time")
                  : (typeof it.minutes_remaining === "number" ? `${Math.max(0, it.minutes_remaining|0)} min left` : "Active");
                return (
                  <tr key={it.id}>
                    <td className="font-medium">{it.name}</td>
                    <td className="text-xs">{(data.sites || {})[it.site_id] || "—"}</td>
                    <td className="text-xs whitespace-nowrap">{fmt(it.prepared_at)}</td>
                    <td className="text-xs whitespace-nowrap">{fmt(it.use_by)}</td>
                    <td className="text-xs">{status}</td>
                    <td className="text-xs">{outcome}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
